/**
 * Client Zip Downloads
 *
 * Builds small browser-side zip archives without adding a dependency.
 *
 * Primary responsibilities:
 * - Fetch URL-backed assets
 * - Package fetched blobs into a zip file
 * - Trigger a browser download for the archive
 *
 * Dependencies:
 * - Browser Blob and URL APIs
 */

export interface ZipAssetSource {
  fileName: string
  fileType?: string | null
  url: string
}

const ZIP_ENCODER = new TextEncoder()
const ZIP_CRC_TABLE = Array.from({ length: 256 }, (_, index) => {
  let value = index

  for (let bit = 0; bit < 8; bit++) {
    value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1
  }

  return value >>> 0
})

const IMAGE_ASSET_TYPES = new Set(["png", "jpg", "jpeg", "webp", "gif", "svg"])

// --------------

export function sanitizeDownloadFileName(value: string) {
  return (
    value
      .trim()
      .replace(/[/\\?%*:|"<>]/g, "-")
      .replace(/\s+/g, " ")
      .slice(0, 140) || "losi-download"
  )
}

// --------------

function getDownloadExtension(fileType?: string | null) {
  const normalized = fileType?.split(";")[0]?.trim().toLowerCase()

  if (!normalized) {
    return null
  }

  if (normalized.includes("/")) {
    const subtype = normalized.split("/")[1]

    if (subtype === "jpeg") return "jpg"
    if (subtype === "svg+xml") return "svg"

    return subtype || null
  }

  if (IMAGE_ASSET_TYPES.has(normalized)) {
    return normalized === "jpeg" ? "jpg" : normalized
  }

  return null
}

// --------------

function withDownloadExtension(fileName: string, fileType?: string | null) {
  const safeName = sanitizeDownloadFileName(fileName)

  if (/\.[a-z0-9]{2,8}$/i.test(safeName)) {
    return safeName
  }

  return `${safeName}.${getDownloadExtension(fileType) || "png"}`
}

// --------------

function getZipDateParts(date = new Date()) {
  const year = Math.max(1980, date.getFullYear())

  return {
    time:
      (date.getHours() << 11) |
      (date.getMinutes() << 5) |
      Math.floor(date.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()
  }
}

// --------------

function getCrc32(data: Uint8Array) {
  let crc = 0xffffffff

  for (const byte of data) {
    crc = ZIP_CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8)
  }

  return (crc ^ 0xffffffff) >>> 0
}

// --------------

function writeZipUInt16(buffer: Uint8Array, offset: number, value: number) {
  buffer[offset] = value & 0xff
  buffer[offset + 1] = (value >>> 8) & 0xff
}

// --------------

function writeZipUInt32(buffer: Uint8Array, offset: number, value: number) {
  buffer[offset] = value & 0xff
  buffer[offset + 1] = (value >>> 8) & 0xff
  buffer[offset + 2] = (value >>> 16) & 0xff
  buffer[offset + 3] = (value >>> 24) & 0xff
}

// --------------

function concatZipParts(parts: Uint8Array[]) {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0)
  const output = new Uint8Array(totalLength)
  let offset = 0

  for (const part of parts) {
    output.set(part, offset)
    offset += part.length
  }

  return output
}

// --------------

export async function buildStoredAssetsZip(sources: ZipAssetSource[]) {
  const localParts: Uint8Array[] = []
  const centralParts: Uint8Array[] = []
  let offset = 0

  for (const [index, source] of sources.entries()) {
    const response = await fetch(source.url)
    if (!response.ok) {
      throw new Error(`Failed to download ${source.fileName}`)
    }

    const data = new Uint8Array(await response.arrayBuffer())
    const responseFileType = response.headers.get("content-type")
    const fileName = `${String(index + 1).padStart(2, "0")}-${sanitizeDownloadFileName(
      withDownloadExtension(source.fileName, source.fileType || responseFileType)
    )}`
    const nameBytes = ZIP_ENCODER.encode(fileName)
    const crc = getCrc32(data)
    const zipDate = getZipDateParts()
    const localHeader = new Uint8Array(30 + nameBytes.length)

    writeZipUInt32(localHeader, 0, 0x04034b50)
    writeZipUInt16(localHeader, 4, 20)
    writeZipUInt16(localHeader, 6, 0)
    writeZipUInt16(localHeader, 8, 0)
    writeZipUInt16(localHeader, 10, zipDate.time)
    writeZipUInt16(localHeader, 12, zipDate.date)
    writeZipUInt32(localHeader, 14, crc)
    writeZipUInt32(localHeader, 18, data.length)
    writeZipUInt32(localHeader, 22, data.length)
    writeZipUInt16(localHeader, 26, nameBytes.length)
    writeZipUInt16(localHeader, 28, 0)
    localHeader.set(nameBytes, 30)

    const centralHeader = new Uint8Array(46 + nameBytes.length)
    writeZipUInt32(centralHeader, 0, 0x02014b50)
    writeZipUInt16(centralHeader, 4, 20)
    writeZipUInt16(centralHeader, 6, 20)
    writeZipUInt16(centralHeader, 8, 0)
    writeZipUInt16(centralHeader, 10, 0)
    writeZipUInt16(centralHeader, 12, zipDate.time)
    writeZipUInt16(centralHeader, 14, zipDate.date)
    writeZipUInt32(centralHeader, 16, crc)
    writeZipUInt32(centralHeader, 20, data.length)
    writeZipUInt32(centralHeader, 24, data.length)
    writeZipUInt16(centralHeader, 28, nameBytes.length)
    writeZipUInt16(centralHeader, 30, 0)
    writeZipUInt16(centralHeader, 32, 0)
    writeZipUInt16(centralHeader, 34, 0)
    writeZipUInt16(centralHeader, 36, 0)
    writeZipUInt32(centralHeader, 38, 0)
    writeZipUInt32(centralHeader, 42, offset)
    centralHeader.set(nameBytes, 46)

    localParts.push(localHeader, data)
    centralParts.push(centralHeader)
    offset += localHeader.length + data.length
  }

  const centralDirectory = concatZipParts(centralParts)
  const end = new Uint8Array(22)
  writeZipUInt32(end, 0, 0x06054b50)
  writeZipUInt16(end, 4, 0)
  writeZipUInt16(end, 6, 0)
  writeZipUInt16(end, 8, sources.length)
  writeZipUInt16(end, 10, sources.length)
  writeZipUInt32(end, 12, centralDirectory.length)
  writeZipUInt32(end, 16, offset)
  writeZipUInt16(end, 20, 0)

  return new Blob([...localParts, centralDirectory, end], {
    type: "application/zip"
  })
}

// --------------

export function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")

  link.href = url
  link.download = sanitizeDownloadFileName(fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
